<script setup lang="ts">
defineProps<{
  embedded: boolean
  isRescheduleMode: boolean
}>()

const emit = defineEmits<{
  'outer-click': []
}>()
</script>

<template>
  <Teleport to="body" :disabled="!embedded">
    <div
      class="schedule-outer"
      :class="{ 'schedule-outer--embedded': embedded }"
      :role="embedded ? 'presentation' : undefined"
    >
      <div v-if="embedded" class="schedule-embed-mask" aria-hidden="true" />
      <div
        class="schedule-embed-shell"
        :class="{ 'schedule-embed-shell--page': !embedded }"
        :role="embedded ? 'dialog' : undefined"
        :aria-modal="embedded ? 'true' : undefined"
        @click.stop
      >
        <div
          class="schedule-page"
          :class="{
            'schedule-page--embedded': embedded,
            'schedule-page--reschedule': isRescheduleMode,
          }"
        >
          <slot />
          <slot name="teleports" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.schedule-outer--embedded {
  position: fixed;
  inset: 0;
  z-index: 12600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  overflow-x: auto;
  overflow-y: auto;
}

.schedule-embed-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  pointer-events: none;
}

.schedule-embed-shell {
  position: relative;
  z-index: 1;
  max-width: 100%;
  max-height: 100%;
  flex-shrink: 0;
  /* 三栏固定宽 205+252+590；窄视口时由外层横向滚动，避免日历区时段被裁切 */
  min-width: 1047px;
}

.schedule-outer--embedded .schedule-embed-shell {
  width: 1047px;
  max-width: 100%;
}

.schedule-embed-shell--page {
  width: 100%;
  height: 100vh;
  min-height: 100vh;
  max-height: none;
  display: flex;
  flex-direction: column;
}

.schedule-outer:not(.schedule-outer--embedded) {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  min-height: 100vh;
}

.schedule-page {
  position: relative;
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', 'Segoe UI', Roboto, Arial,
    sans-serif;
  box-sizing: border-box;
}

.schedule-embed-shell--page .schedule-page {
  flex: 1;
  min-height: 0;
  height: auto;
}

.schedule-page--embedded {
  width: 1047px;
  max-width: min(1047px, 100%);
  height: min(710px, calc(100vh - 48px));
  min-height: 0;
  max-height: calc(100vh - 48px);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.schedule-page--embedded :deep(.schedule-body) {
  display: grid;
  flex: 0 0 638px;
  height: 638px;
  min-height: 638px;
  max-height: 638px;
  margin-top: 0;
  padding: 0;
  box-sizing: border-box;
  grid-template-columns: 205px 252px 590px;
  align-items: start;
  gap: 0;
  overflow: hidden;
}

.schedule-page--embedded :deep(.schedule-body--loading),
.schedule-page :deep(.schedule-body--loading) {
  display: flex;
  align-items: center;
  justify-content: center;
  grid-template-columns: none;
  background: #ffffff;
}

/* slot 内容在父组件，须 :deep 才能命中 */
.schedule-page :deep(.schedule-body) {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: grid;
 /*  grid-template-columns: minmax(200px, 240px) minmax(220px, 280px) 1fr; */
  gap: 0;
}

.schedule-page :deep(.schedule-panel) {
  min-height: 0;
  box-sizing: border-box;
}

.schedule-page--embedded :deep(.schedule-panel--calendar) {
  --cal-month-top: 56px;
  --cal-month-row-h: 24px;
  --cal-month-to-week-gap: 16px;
  --cal-week-strip-top: calc(var(--cal-month-top) + var(--cal-month-row-h) + var(--cal-month-to-week-gap));
  position: relative;
  align-self: start;
  display: block;
  width: 590px;
  min-width: 590px;
  max-width: 590px;
  height: 638px;
  min-height: 638px;
  max-height: 638px;
  padding: 0;
  background: #f5f6fb;
  box-sizing: border-box;
  overflow: hidden;
}

.schedule-page :deep(.schedule-panel--calendar) {
  position: relative;
  overflow: hidden;
  background: #f5f6fb;
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
}

@media (max-width: 1100px) {
  .schedule-page:not(.schedule-page--embedded) :deep(.schedule-body) {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 220px) minmax(0, 220px) minmax(0, 1fr);
    overflow: hidden;
  }

  .schedule-page:not(.schedule-page--embedded) :deep(.schedule-panel--list) {
    max-height: none;
    overflow: hidden;
  }

  .schedule-page:not(.schedule-page--embedded) :deep(.schedule-panel--list > .unscheduled-list) {
    overflow-y: auto;
  }

  .schedule-page:not(.schedule-page--embedded) :deep(.schedule-panel--inspectors) {
    max-height: none;
    overflow-y: auto;
  }

  .schedule-page:not(.schedule-page--embedded) :deep(.schedule-panel--calendar) {
    min-height: 0;
    overflow: hidden;
  }
}

</style>
